"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../utils"

const keyboardShortcutVariants = cva(
  "pointer-events-none inline-flex items-center rounded-sm font-mono text-xs/none font-medium select-none gap-0.5",
  {
    variants: {
      variant: {
        default: "border border-border bg-background text-foreground",
        secondary: "bg-muted text-muted-foreground",
        inverse: "bg-primary-active border border-muted-foreground text-primary-foreground",
      },
      size: {
        default: "px-1 py-0.5",
        lg: "px-2 py-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface KeyboardShortcutProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof keyboardShortcutVariants> {
  keys: string | string[]
}

const KeyboardShortcut = React.forwardRef<HTMLElement, KeyboardShortcutProps>(
  ({ className, variant, size, keys, ...props }, ref) => {
    const isWindows = typeof navigator !== "undefined" && navigator.platform.includes("Win")

    const normalizeKey = (key: string) => {
      return key === "⌘" && isWindows ? "^" : key
    }

    const keyArray = Array.isArray(keys) ? keys.map(normalizeKey) : [normalizeKey(keys)]

    // For sequential shortcuts (multiple separate keys), render multiple kbd elements
    if (
      Array.isArray(keys) &&
      keys.length > 1 &&
      !keys.some((k) => k.includes("+") || k.includes("⌘") || k.includes("⌃") || k.includes("⇧"))
    ) {
      return (
        <div className="inline-flex items-center gap-1" ref={ref as React.Ref<HTMLDivElement>} {...props}>
          {keyArray.map((key, index) => (
            <kbd key={`${key}-${index}`} className={cn(keyboardShortcutVariants({ variant, size, className }))}>
              <span>{key}</span>
            </kbd>
          ))}
        </div>
      )
    }

    // For single keys or modifier combinations, render as before
    return (
      <kbd className={cn(keyboardShortcutVariants({ variant, size, className }))} ref={ref} {...props}>
        {keyArray.map((key, index) => (
          <span key={`${key}-${index}`}>{key}</span>
        ))}
      </kbd>
    )
  }
)
KeyboardShortcut.displayName = "KeyboardShortcut"

export { KeyboardShortcut, keyboardShortcutVariants }
